import { useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import { supabase } from "../config/supabaseClient";
import { Form, Input, Button, Typography, Divider, Card, App } from "antd";
import { MailOutlined, LockOutlined, UserOutlined } from "@ant-design/icons";
import signupImage from "../assets/Homepage.jpeg";

const { Title, Text } = Typography;

const ROLE_LABEL = { student: "Student", teacher: "Teacher" };

export default function SignupForm({ role }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const roleLabel = ROLE_LABEL[role];
  const { message } = App.useApp();

  const handleSignup = async (values) => {
    const { name, email, password } = values;
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name, type: role },
        },
      });

      if (error) {
        message.error(error.message);
        return;
      }

      if (!data.session) {
        message.success("Account created! Please check your email to confirm, then login.");
      } else {
        message.success("Account created successfully!");
      }

      navigate(`/login/${role}`);
    } catch (error) {
      console.error("Error during signup:", error);
      message.error("Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-background to-muted">
      <div className="flex flex-col lg:flex-row w-full max-w-7xl bg-white shadow-lg overflow-hidden">
        <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-200">
          <div className="w-full max-w-lg">
            <Card className="shadow-soft rounded-2xl bg-white animate-scale-in" style={{ borderRadius: "16px" }}>
              <Title level={2} className="text-center text-gray-800 text-xl md:text-2xl">
                {roleLabel} Sign Up
              </Title>
              <Divider />
              <Text type="none" className="block text-center text-foreground text-sm md:text-base">
                Create your {roleLabel} account to get started.
              </Text>

              <Form className="py-4 bg-gray-100 px-2 rounded-lg" layout="vertical" onFinish={handleSignup}>
                <Form.Item label="Full Name" name="name" rules={[{ required: true, message: "Please enter your name!" }]}>
                  <Input prefix={<UserOutlined className="text-gray-400" />} placeholder="Enter your name" className="rounded-lg py-2" />
                </Form.Item>

                <Form.Item label="Email" name="email" rules={[{ required: true, type: "email", message: "Please enter a valid email!" }]}>
                  <Input prefix={<MailOutlined className="text-gray-400" />} placeholder="Enter your email" className="rounded-lg py-2" />
                </Form.Item>

                <Form.Item label="Password" name="password" rules={[{ required: true, min: 6, message: "Password must be at least 6 characters!" }]}>
                  <Input.Password prefix={<LockOutlined className="text-gray-400" />} placeholder="Enter your password" className="rounded-lg py-2" />
                </Form.Item>

                <Form.Item
                  label="Confirm Password"
                  name="confirm"
                  dependencies={["password"]}
                  rules={[
                    { required: true, message: "Please confirm your password!" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Passwords do not match!"));
                      },
                    }),
                  ]}
                >
                  <Input.Password prefix={<LockOutlined className="text-gray-400" />} placeholder="Confirm your password" className="rounded-lg py-2" />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="none"
                    className="bg-primary py-5 text-white font-semibold hover:opacity-90 rounded-lg"
                    htmlType="submit"
                    loading={loading}
                    block
                  >
                    Sign Up
                  </Button>
                </Form.Item>
              </Form>

              <div className="text-center">
                <Text type="secondary" className="text-sm">
                  Already have an account?{" "}
                  <Link to={`/login/${role}`} className="text-primary font-semibold">
                    Login as {roleLabel}
                  </Link>
                </Text>
              </div>
            </Card>
          </div>
        </div>

        <div className="w-full lg:w-1/2 relative hidden lg:block overflow-hidden">
          <div className="absolute bottom-0 left-0 w-full h-full bg-gradient-to-t from-primary/85 to-transparent flex items-end justify-center z-10 p-6">
            <div className="text-center text-white pb-[50px]">
              <h2 className="text-2xl font-bold mb-2 md:text-2xl">Join Our Learning Platform</h2>
              <p className="text-sm md:text-lg">
                Create your account to access personalized dashboards, courses, and track your progress with ease.
              </p>
            </div>
          </div>
          <img src={signupImage} alt="Learning Management System" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
        </div>
      </div>
    </div>
  );
}
